import { ArticleInlineByline } from 'components/Article/ArticleInlineByline';
import { SkipToTableOfContentsOverlay } from 'components/Article/SkipToTableOfContentsOverlay';
import { BookmarkButton } from 'components/BookmarkButton';
import { SocialShareMenu } from 'components/SocialShareMenu';
import { isShellArticle } from 'lib/article';
import { MYNEWS_ENABLED } from 'lib/brandFeatures';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { ARTICLE } from 'lib/myNewsConstants';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import classNames from 'classnames';
import React from 'react';
import { ContentTimestampWithSource } from 'components/ContentTimestampWithSource';

/**
 * StandardByline component renders the byline section of an article.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.isLiveBlog - Indicates if the article is a live blog.
 * @param {boolean} props.showUserActions - Indicates if user actions should be shown.
 * @param {boolean} props.showBylineTimestamp - Indicates if the byline timestamp should be shown.
 * @param {boolean} props.showInlineByline - Indicates if the inline byline should be shown.
 * @param {import('lib/graphqlTypeDefs.mjs').VerticalType} props.vertical - The vertical of the article.
 * @param {string} props.bylineTimestampDatePublished - The date the byline was published.
 * @param {import('lib/graphqlTypeDefs.mjs').Article} props.article - The article object.
 * @param {string} props.contentId - The content ID of the article.
 * @param {boolean} props.tableOfContentsEnabled - Indicates if the table of contents is enabled.
 * @param {boolean} props.showCreatedDate - Indicates if the created date should be shown.
 * @param {boolean} props.isChromeless - Indicates if the current rendering context is the
 * "chromeless render" (used by mobile app)
 * @returns {React.ReactElement|null} The rendered component or null if the article is a shell article.
 */
export function StandardByline({
  isLiveBlog = false,
  showUserActions = false,
  showBylineTimestamp = false,
  showInlineByline = false,
  vertical = '',
  bylineTimestampDatePublished = '',
  article,
  contentId,
  tableOfContentsEnabled = false,
  showCreatedDate = false,
  isChromeless = false,
}) {
  const {
    ecommerceEnabled,
    headline,
    source,
    taxonomy,
    url,
    date: { createdAt: dateCreated, publishedAt: articleDatePublished },
    breakingNews,
    authors: articleAuthors = [],
  } = article;

  const featureFlags = useFeatureFlagContext();

  if (isShellArticle(article)) {
    return null;
  }

  const authors = articleAuthors?.filter(({ authorType }) => authorType === 'author') || [];

  const { organization: { name: orgName, externalUrl: orgUrl } = {} } = source || {};

  const isLiveBreakingNews = breakingNews || isLiveBlog;
  const showSubline = showUserActions || showBylineTimestamp || showInlineByline;
  const myNewsConfig = getFeatureConfigForBrand(MYNEWS_ENABLED, vertical, featureFlags);
  const { showBookmark = false } = myNewsConfig || {};
  const datePublished = bylineTimestampDatePublished || articleDatePublished;

  return (
    <>
      {showSubline && (
        <section className={classNames({
          mb7: !ecommerceEnabled && !isLiveBreakingNews,
          mb6: !ecommerceEnabled && isLiveBreakingNews,
        })}
        >
          {showUserActions && !showBookmark ? (
            <div className="article-social-share-top" data-testid="article-body-social-share-menu">
              <SocialShareMenu
                url={url?.primary}
                headline={headline.tease}
                contentId={contentId}
                pageRegion="article-top"
                trackingEventName="article_social_share"
              />
            </div>
          ) : null}

          {vertical === 'news' && orgName === 'NBC Sports' && <div className="sports-article-top-badge" />}

          {showUserActions && showBookmark ? (
            <div className="article-bookmark-menu" data-testid="article-body-bookmark-menu">
              <BookmarkButton
                contentId={contentId}
                contentType={ARTICLE}
                pageRegion="article-top"
                isChromeless={isChromeless}
              />
            </div>
          ) : null}

          {showBylineTimestamp && (
            <ContentTimestampWithSource
              dateCreated={dateCreated}
              datePublished={datePublished}
              orgName={orgName}
              orgUrl={orgUrl}
              showCreatedDate={showCreatedDate}
              source={source}
              testId="article-body-timestamp"
            />
          )}
          {showInlineByline && (
            <div className="relative">
              <ArticleInlineByline
                authors={authors}
                vertical={vertical}
                taxonomy={taxonomy}
              />
              {tableOfContentsEnabled && <SkipToTableOfContentsOverlay className="byline" />}
            </div>
          )}
        </section>
      )}
    </>
  );
}
